import { useEffect } from 'react';
import useApi from './useApi';
import { useToast } from '../app/useToast';
import useApiEndpoints from '../context/useApiEndpoints';
import { useVerificationStore } from '../context/VerificationContext';
import { createReportMetadataList } from '../factory/Verification/ReportMetadataFactory';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import { DateTimeFormat, formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { DNVReportType } from '../enums/dnvReportTypeEnum';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, getApiData } = useApi();
    const { setReportMetadataList, selectedReportMetadata } = useVerificationStore();
    const { toastError } = useToast();

    const getReportMetadataList = () => {
        getApiData({
            endpoint: apiEndpoints['getDNVReportsMetadata'].url,
        });
    };

    const getReportMetadataListForShip = (imoNumber: string) => {
        getApiData({
            endpoint: apiEndpoints['getDNVReportsMetadataForShip'].url,
            queryParam: buildQueryParams({ imoNumber }),
        });
    };

    const getGeneratedDnvReport = (reportName: string) => {
        getApiData({
            endpoint: apiEndpoints['getDnvReport'].url,
            queryParam: buildQueryParams({ reportName }),
        });
    };
    const getOvdDnvReport = (imoNumber: number, reportType: DNVReportType, startDate: string, endDate: string) => {
        getApiData({
            endpoint: apiEndpoints['getOvdDnvReport'].url,
            queryParam: buildQueryParams({ imoNumber, reportType, startDate, endDate }),
            additionalInfo: { startDate: startDate, endDate: endDate },
        });
    };

    const importMissingVesselPositions = async (imoNumber: number, fromDate: Date, toDate: Date) => {
        const from = formatDateTime(fromDate, DateTimeFormat.queryFormats.dateTime);
        const to = formatDateTime(toDate, DateTimeFormat.queryFormats.dateTime);
        const result = await getApiData({
            endpoint: apiEndpoints['importMissingVesselPositions'].url,
            queryParam: buildQueryParams({ imoNumber, from, to }),
        });

        if (result) return result;
    };

    const downloadFile = async (data: any[], fileName: string) => {
        const url = URL.createObjectURL(new Blob([jsonToCsv(data)], { type: 'application/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    };

    useEffect(() => {
        if (state.status === 'success') {
            if (state.endpoint === apiEndpoints['getDNVReportsMetadata'].url) {
                const _reportMetadataListBM = state.result as any;
                const _reportMetadataList = createReportMetadataList(_reportMetadataListBM);
                setReportMetadataList([..._reportMetadataList]);
            } else if (state.endpoint === apiEndpoints['getDNVReportsMetadataForShip'].url) {
                const _reportMetadataListBM = state.result as any;
                const _reportMetadataList = createReportMetadataList(_reportMetadataListBM);
                setReportMetadataList([..._reportMetadataList]);
            } else if (state.endpoint === apiEndpoints['getDnvReport'].url) {
                const data = state.result as [];
                if (data.length == 0) {
                    toastError('Failed to download data from DNV');
                    return;
                }
                const fileName = selectedReportMetadata?.latestReportFileName?.replace('.json', '.csv') ?? `dnvreport.csv`;
                downloadFile(data, fileName);
            } else if (state.endpoint === apiEndpoints['getOvdDnvReport'].url) {
                const data = state.result as [];
                if (data.length == 0) {
                    toastError('Failed to download data from DNV');
                    return;
                }
                const startDate = state.additionalInfo.startDate;
                const reportType = selectedReportMetadata.reportType === DNVReportType.OVDLA ? 'Log Abstract' : 'Bunker';
                let fileName = selectedReportMetadata?.vesselName ? selectedReportMetadata?.vesselName : '';
                fileName += '_' + reportType + '_' + startDate?.substring(0, 4) + '.csv';

                downloadFile(data, fileName);
            }
        } else {
            processApiEndpointError(state);
        }
    }, [state]);

    const jsonToCsv = (items) => {
        const header = Object.keys(items[0]);
        const replacer = (key, value) => value ?? '';
        const rowItems = items.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(';'));
        return [...rowItems].join('\r\n');
    };

    return {
        state,
        getReportMetadataList,
        getReportMetadataListForShip,
        getGeneratedDnvReport,
        getOvdDnvReport,
        importMissingVesselPositions,
    };
};
